import React, { useState } from 'react';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Snackbar,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LogoutIcon from '@mui/icons-material/Logout';
import RefreshIcon from '@mui/icons-material/Refresh';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { DataGrid } from '@mui/x-data-grid';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { bundleApi } from '../services/bundleApi';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatBytes(bytes) {
  if (bytes == null) return '—';
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, exp)).toFixed(2)} ${units[exp]}`;
}

function fileBasename(name) {
  return name.split('/').pop() || name;
}

// ── Componente principal ─────────────────────────────────────────────────────

function BundlePage() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  // Estado de descarga: { [blobName]: 'loading' | 'error' }
  const [downloadState, setDownloadState] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const { data, isLoading, error, refetch } = useQuery(
    'bundleFiles',
    () => bundleApi.list().then((r) => r.data),
  );

  const handleDownload = async (blobName) => {
    setDownloadState((prev) => ({ ...prev, [blobName]: 'loading' }));
    try {
      const { data: result } = await bundleApi.signedUrl(blobName);
      // Dispara la descarga directamente desde GCS sin pasar por el backend
      const link = document.createElement('a');
      link.href = result.url;
      link.download = fileBasename(blobName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setDownloadState((prev) => {
        const next = { ...prev };
        delete next[blobName];
        return next;
      });
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Error al generar la URL de descarga.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
      setDownloadState((prev) => {
        const next = { ...prev };
        delete next[blobName];
        return next;
      });
    }
  };

  const files = data?.files ?? [];

  const columns = [
    {
      field: 'name',
      headerName: 'Fichero',
      flex: 2,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography variant="body2" noWrap>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'size',
      headerName: 'Tamaño',
      width: 130,
      valueFormatter: (value) => formatBytes(value),
    },
    {
      field: 'updated',
      headerName: 'Última modificación',
      width: 200,
      valueFormatter: (value) => (value ? new Date(value).toLocaleString() : '—'),
    },
    {
      field: 'content_type',
      headerName: 'Tipo',
      width: 160,
      renderCell: (params) => (
        <Chip label={params.value} size="small" variant="outlined" />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const loading = downloadState[params.row.name] === 'loading';
        return (
          <Button
            size="small"
            variant="contained"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <CloudDownloadIcon />}
            disabled={loading}
            onClick={() => handleDownload(params.row.name)}
          >
            {loading ? 'Preparando…' : 'Descargar'}
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Tooltip title="Volver a Jobs">
            <IconButton color="inherit" edge="start" onClick={() => navigate('/jobs')} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Bundle Downloads — {data?.bucket ?? 'itr_bundle'}
          </Typography>
          <Typography variant="body2" sx={{ mr: 2, opacity: 0.8 }}>
            {user?.username}
          </Typography>
          <Tooltip title="Review Output">
            <IconButton color="inherit" onClick={() => navigate('/review')}>
              <AssignmentIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Progress Dashboard">
            <IconButton color="inherit" onClick={() => navigate('/dashboard')}>
              <DashboardIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refrescar listado">
            <IconButton color="inherit" onClick={() => refetch()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Logout">
            <IconButton color="inherit" edge="end" onClick={logout}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">
            Ficheros disponibles
            {files.length > 0 && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({files.length} fichero{files.length !== 1 ? 's' : ''})
              </Typography>
            )}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error al cargar el listado: {error.response?.data?.detail || error.message}
          </Alert>
        )}

        <DataGrid
          rows={files}
          getRowId={(row) => row.name}
          columns={columns}
          loading={isLoading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default BundlePage;
